"use client";

import { useState } from "react";
import Link from "next/link";

export function ContactForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState<{
    hasAccount: boolean;
    accessToken?: string;
  } | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const res = await fetch("/api/public/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          phone,
          companyName,
          subject,
          message,
          website,
          source: "scaffer_home",
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error ?? "Could not send message");
      setDone({
        hasAccount: Boolean(data.hasAccount),
        accessToken: typeof data.accessToken === "string" ? data.accessToken : undefined,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not send message");
    } finally {
      setSaving(false);
    }
  }

  if (done) {
    return (
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-6">
        <h2 className="text-lg font-semibold text-emerald-950">Thanks — we&apos;ve got it</h2>
        {done.hasAccount ? (
          <p className="mt-2 text-sm text-emerald-900">
            You already have a ScaffOps account, so we emailed{" "}
            <strong>help@scaffops.co.uk</strong> and pointed you to sign in rather than a guest
            link.{" "}
            <Link href="/login" className="font-semibold underline hover:no-underline">
              Sign in
            </Link>{" "}
            to continue in Help, or wait for our reply by email.
          </p>
        ) : (
          <p className="mt-2 text-sm text-emerald-900">
            We&apos;ve emailed you a private link to view this ticket and add updates. Check your
            inbox (and spam). Our team at help@scaffops.co.uk has been notified.
          </p>
        )}
        {done.accessToken ? (
          <Link
            href={`/contact/ticket/${done.accessToken}`}
            className="mt-4 inline-flex rounded-xl bg-brand-dark px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-dark/90"
          >
            View your ticket
          </Link>
        ) : null}
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="relative space-y-4"
      toolname="submit_contact_request"
      tooldescription="Submit a support or sales message to ScaffOps. Creates a ticket and notifies the team."
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="font-medium text-zinc-800">Name *</span>
          <input
            required
            name="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full rounded-lg border border-zinc-300 px-3 py-2"
            autoComplete="name"
            toolparamdescription="Full name of the person contacting ScaffOps"
          />
        </label>
        <label className="block text-sm">
          <span className="font-medium text-zinc-800">Email *</span>
          <input
            required
            name="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 w-full rounded-lg border border-zinc-300 px-3 py-2"
            autoComplete="email"
            toolparamdescription="Reply email address"
          />
        </label>
        <label className="block text-sm">
          <span className="font-medium text-zinc-800">Phone</span>
          <input
            name="phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="mt-1 w-full rounded-lg border border-zinc-300 px-3 py-2"
            autoComplete="tel"
            toolparamdescription="Optional phone number"
          />
        </label>
        <label className="block text-sm">
          <span className="font-medium text-zinc-800">Company</span>
          <input
            name="companyName"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            className="mt-1 w-full rounded-lg border border-zinc-300 px-3 py-2"
            autoComplete="organization"
            toolparamdescription="Optional scaffolding company or organisation name"
          />
        </label>
      </div>
      <label className="block text-sm">
        <span className="font-medium text-zinc-800">Subject *</span>
        <input
          required
          name="subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className="mt-1 w-full rounded-lg border border-zinc-300 px-3 py-2"
          placeholder="e.g. Billing question, demo request…"
          toolparamdescription="Short subject line for the enquiry"
        />
      </label>
      <label className="block text-sm">
        <span className="font-medium text-zinc-800">Message *</span>
        <textarea
          required
          name="message"
          rows={6}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="mt-1 w-full rounded-lg border border-zinc-300 px-3 py-2"
          placeholder="How can we help?"
          toolparamdescription="Full message describing the question or request"
        />
      </label>
      {/* Honeypot */}
      <label className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden" aria-hidden>
        Website
        <input tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
      </label>
      {error ? (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950">
          {error}
        </p>
      ) : null}
      <button
        type="submit"
        disabled={saving}
        className="inline-flex rounded-xl bg-brand-yellow px-6 py-3 font-semibold text-brand-dark hover:bg-brand-yellow/90 disabled:opacity-60"
      >
        {saving ? "Sending…" : "Send message"}
      </button>
      <p className="text-xs text-zinc-500">
        We&apos;ll create a support ticket and notify help@scaffops.co.uk. If you don&apos;t have an
        account, you&apos;ll get a private link to track the ticket.
      </p>
    </form>
  );
}
